class CryptoLib {
    static ALGORITHM = 'AES-GCM';
    static KEY_LENGTH = 256;
    static IV_LENGTH = 12; // Recommended IV size for GCM
    password;

    constructor(password) {
        this.password = password;
    }

    // Convert text to Uint8Array
    static encodeText(text) {
        return new TextEncoder().encode(text);
    }

    // Convert Uint8Array to base64
    static arrayBufferToBase64(buffer) {
        return btoa(String.fromCharCode(...new Uint8Array(buffer)));
    }

    // Convert base64 to Uint8Array
    static base64ToArrayBuffer(base64) {
        return new Uint8Array([...atob(base64)].map(c => c.charCodeAt(0)));
    }

    // Generate cryptographic key from password using PBKDF2
    async deriveKey(salt) {
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            CryptoLib.encodeText(this.password),
            { name: 'PBKDF2' },
            false,
            ['deriveKey']
        );

        return crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 100000,
                hash: 'SHA-256'
            },
            keyMaterial,
            { name: CryptoLib.ALGORITHM, length: CryptoLib.KEY_LENGTH },
            true,
            ['encrypt', 'decrypt']
        );
    }

    // Encrypt data
    async encrypt(text) {
        const iv = crypto.getRandomValues(new Uint8Array(CryptoLib.IV_LENGTH));
        const salt = crypto.getRandomValues(new Uint8Array(16));
        const key = await this.deriveKey(salt);

        const encrypted = await crypto.subtle.encrypt(
            { name: CryptoLib.ALGORITHM, iv: iv },
            key,
            CryptoLib.encodeText(text)
        );

        // Concatenate salt + IV + encrypted data
        const combinedData = new Uint8Array([...salt, ...iv, ...new Uint8Array(encrypted)]);
        return CryptoLib.arrayBufferToBase64(combinedData);
    }

    // Decrypt data
    async decrypt(encryptedData) {
        const data = CryptoLib.base64ToArrayBuffer(encryptedData);
        const salt = data.slice(0, 16);
        const iv = data.slice(16, 16 + CryptoLib.IV_LENGTH);
        const encryptedText = data.slice(16 + CryptoLib.IV_LENGTH);

        const key = await this.deriveKey(salt);

        const decrypted = await crypto.subtle.decrypt(
            { name: CryptoLib.ALGORITHM, iv: iv },
            key,
            encryptedText
        );

        return new TextDecoder().decode(decrypted);
    }
}

export class CryptoService {

    cryptoService;

    constructor(password) {
        this.cryptoService = new CryptoLib(password);
    }

    /**
     * 
     * @param text 
     * @returns 
     */
    async encryptData(text: string = '') {
        try {
            return await this.cryptoService.encrypt(text);
        } catch (error) {
            console.error('Encryption failed:', error);
            return null;
        }
    }

    /**
     * 
     * @param encryptedText 
     * @returns 
     */
    async decryptData(encryptedText: string = '') {
        try {
            return await this.cryptoService.decrypt(encryptedText);
        } catch (error) {
            console.error('Decryption failed:', error);
            return null;
        }
    }
}
/// example
// (async () => {
//     const password = 'StrongPassword123!';
//     const cryptoController = new CryptoService(password);

//     const secretText = 'This is a secret message!';

//     const encryptedText = await cryptoController.encryptData(secretText);
//     console.log('🔐 Encrypted:', encryptedText);

//     const decryptedText = await cryptoController.decryptData(encryptedText);
//     console.log('🔓 Decrypted:', decryptedText);
// })();